import { useState, type ReactElement } from 'react';
import { useI18n } from '../../lib/i18n/I18nProvider';
import { newIntent, useApiAction, useApiList, useApiResource } from '../../lib/api/queries';
import type { Membership, Organization } from '../../lib/api/types';
import { Badge, Card, DataTable, Field, Ltr, PageTitle, Select } from '../../ui/primitives';
import { EmptyState, ErrorState, LoadingState } from '../../ui/states';
import { ApprovalHandoff, AuditStatement, RefusalWithRemedy, StepUpPanel } from '../../ui/guards';
import { useSession } from '../../lib/auth/session';
import type { HandoffGrant, HandoffRequest } from '../../lib/auth/handoff';
import { statusTone } from './Expos';

const ORG_TYPES = ['developer', 'brokerage', 'platform'];
const REASON_CODES = ['onboarding', 'operational', 'compliance', 'security', 'administrative_error'];

/** A protected global-role grant needs a second approval bound to the exact roles and scopes. */
const ROLE_ASSIGN = 'user.role.assign';

/** `ADM-13` — organizations, memberships and role assignment. */
export function AdminOrganizations(): ReactElement {
  const { t, bilingual } = useI18n();
  const orgs = useApiList<Organization>('/v1/admin/organizations');
  const create = useApiAction<Organization>(['/v1/admin/organizations']);
  const [selected, setSelected] = useState<string | null>(null);

  const [type, setType] = useState(ORG_TYPES[0] ?? 'developer');
  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [display, setDisplay] = useState('');
  const [country, setCountry] = useState('SA');

  return (
    <>
      <PageTitle title={t('admin.orgs.title')}>{t('admin.orgs.desc')}</PageTitle>

      <Card title={t('admin.orgs.title')}>
        {orgs.isLoading ? <LoadingState /> : null}
        {orgs.error ? <ErrorState error={orgs.error} onRetry={() => void orgs.refetch()} /> : null}
        {orgs.data ? (
          <DataTable<Organization>
            caption={t('admin.orgs.title')}
            rows={orgs.data.items ?? []}
            rowKey={(o) => o.organization_id}
            empty={<EmptyState>{t('admin.orgs.desc')}</EmptyState>}
            columns={[
              {
                key: 'name',
                header: t('table.name'),
                cell: (o) => (
                  <button
                    type="button"
                    className="btn btn--secondary btn--small"
                    onClick={() => setSelected(o.organization_id)}
                  >
                    {o.display_name || bilingual(o.legal_name_ar, o.legal_name_en)}
                  </button>
                ),
              },
              { key: 'type', header: t('table.kind'), cell: (o) => <Ltr>{o.type}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (o) => <Badge tone={statusTone(o.status)}>{o.status}</Badge>,
              },
              { key: 'verify', header: 'verification_state', cell: (o) => <Ltr>{o.verification_state}</Ltr> },
              { key: 'country', header: 'country', cell: (o) => <Ltr>{o.country}</Ltr> },
            ]}
          />
        ) : null}
      </Card>

      <Card title={t('action.create')}>
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            create.mutate(newIntent({
              path: '/v1/admin/organizations',
              body: {
                type,
                legal_name_ar: nameAr,
                legal_name_en: nameEn,
                display_name: display,
                country,
              },
            }));
          }}
        >
          <Select
            label="type"
            name="org-type"
            value={type}
            options={ORG_TYPES.map((x) => ({ value: x, label: x }))}
            onChange={setType}
          />
          <Field label="legal_name_ar" name="org-ar" value={nameAr} onChange={setNameAr} required />
          <Field label="legal_name_en" name="org-en" value={nameEn} onChange={setNameEn} required ltr />
          <Field label="display_name" name="org-display" value={display} onChange={setDisplay} required />
          <Field label="country" name="org-country" value={country} onChange={setCountry} required ltr />
          {create.error ? <ErrorState error={create.error} /> : null}
          <button className="btn" type="submit" disabled={create.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>

      {selected ? <Memberships organizationId={selected} /> : null}
    </>
  );
}

function Memberships({ organizationId }: { organizationId: string }): ReactElement {
  const { t } = useI18n();
  const session = useSession();
  const path = `/v1/admin/organizations/${organizationId}/memberships`;
  const orgPath = `/v1/admin/organizations/${organizationId}`;
  const memberships = useApiList<Membership>(path);
  // `handleOrganizationAction` calls `ifMatchTransition`: BOTH `:transition` and `:verify` are
  // compare-and-swap and refuse a request with no `If-Match`. The screen therefore has to read the
  // organization's version before it can change its lifecycle at all.
  const organization = useApiResource<Organization>(orgPath);
  const invite = useApiAction<Membership>([path]);
  const action = useApiAction<Membership>([path]);
  const orgAction = useApiAction<Organization>(['/v1/admin/organizations', orgPath]);

  const [userId, setUserId] = useState('');
  const [roleCodes, setRoleCodes] = useState('');
  const [reason, setReason] = useState(REASON_CODES[0] ?? 'onboarding');
  const [target, setTarget] = useState<Membership | null>(null);
  const [grant, setGrant] = useState<HandoffGrant | null>(null);

  const roles = roleCodes
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const orgVersion = organization.data?.version ?? organization.data?.data.version ?? null;
  const stepUp = session.grantFor(ROLE_ASSIGN);

  /**
   * The approval names the COMPLETE proposed post-state, not a subset.
   *
   * `iam.CreateApproval` computes `rolepolicy.RoleGrantDigest` over the roles AND the scopes and
   * binds the approval to it; the assign-roles request sends `participation_ids` and `expo_ids`
   * too, so an approval that named only the role codes bound to a different digest and could never
   * be spent. Both halves now carry the same union.
   */
  const proposal = target
    ? {
        role_codes: roles,
        participation_ids: target.participation_ids ?? [],
        expo_ids: target.expo_ids ?? [],
      }
    : null;

  const handoff: HandoffRequest | null =
    target && proposal && roles.length > 0
      ? {
          action: ROLE_ASSIGN,
          aggregateType: 'membership',
          aggregateId: target.membership_id,
          expectedVersion: target.version,
          reasonCode: reason,
          proposal,
          label: `assign ${roles.join(', ')} on membership ${target.membership_id}`,
        }
      : null;

  const assignRoles = (): void => {
    if (!target || !proposal) return;
    action.mutate(
      newIntent({
        path: `/v1/admin/memberships/${target.membership_id}:assign-roles`,
        action: ROLE_ASSIGN,
        label: `assign-roles ${target.membership_id}`,
        body: { ...proposal, reason_code: reason },
        ifMatch: target.version,
      }),
    );
  };

  return (
    <>
      <Card title={t('admin.memberships.title')} description={t('admin.memberships.desc')}>
        {memberships.isLoading ? <LoadingState /> : null}
        {memberships.error ? <ErrorState error={memberships.error} onRetry={() => void memberships.refetch()} /> : null}
        {memberships.data ? (
          <DataTable<Membership>
            caption={t('admin.memberships.title')}
            rows={memberships.data.items ?? []}
            rowKey={(m) => m.membership_id}
            empty={<EmptyState>{t('admin.memberships.desc')}</EmptyState>}
            columns={[
              { key: 'user', header: 'user_id', cell: (m) => <Ltr>{m.user_id}</Ltr> },
              { key: 'roles', header: 'role_codes', cell: (m) => <Ltr>{(m.role_codes ?? []).join(', ')}</Ltr> },
              {
                key: 'status',
                header: t('table.status'),
                cell: (m) => <Badge tone={statusTone(m.status)}>{m.status}</Badge>,
              },
              {
                key: 'version',
                header: t('admin.expos.version'),
                cell: (m) => <span className="numeral">{m.version}</span>,
              },
              {
                key: 'actions',
                header: t('table.actions'),
                cell: (m) => (
                  <span className="row">
                    <button
                      type="button"
                      className="btn btn--secondary btn--small"
                      onClick={() => {
                        setTarget(m);
                        setRoleCodes((m.role_codes ?? []).join(', '));
                      }}
                    >
                      assign-roles
                    </button>
                    {['activate', 'suspend', 'close'].map((verb) => (
                      <button
                        key={verb}
                        type="button"
                        className="btn btn--secondary btn--small"
                        disabled={action.isPending}
                        onClick={() =>
                          action.mutate(newIntent({
                            path: `/v1/admin/memberships/${m.membership_id}:${verb}`,
                            body: { reason_code: reason },
                            ifMatch: m.version,
                          }))
                        }
                      >
                        {verb}
                      </button>
                    ))}
                  </span>
                ),
              },
            ]}
          />
        ) : null}
      </Card>

      {target ? (
        <Card title={`assign-roles · ${target.membership_id.slice(0, 8)}`}>
          <AuditStatement>
            <p>
              <Ltr>user.role.assign</Ltr> — a protected global role needs a second approval bound to
              the exact roles and scopes below, and <Ltr>If-Match=&quot;{target.version}&quot;</Ltr>{' '}
              makes a concurrent change a conflict rather than an overwrite.
            </p>
          </AuditStatement>
          <Field label="role_codes" name="role-codes" value={roleCodes} onChange={setRoleCodes} ltr />
          <Select
            label={t('guard.reason')}
            name="membership-reason"
            value={reason}
            options={REASON_CODES.map((c) => ({ value: c, label: c }))}
            onChange={setReason}
          />
          {stepUp ? null : <StepUpPanel action={ROLE_ASSIGN} />}
          <ApprovalHandoff request={handoff} onGranted={setGrant} />
          {action.error ? (
            <RefusalWithRemedy
              error={action.error}
              action={action.lastIntent.current?.action ?? ROLE_ASSIGN}
              retryExact={action.retryLast}
              onReread={() => {
                setGrant(null);
                setTarget(null);
                void memberships.refetch();
              }}
            />
          ) : null}
          <button
            className="btn"
            type="button"
            disabled={action.isPending || roles.length === 0 || !grant}
            onClick={assignRoles}
          >
            {t('action.save')}
          </button>
        </Card>
      ) : null}

      <Card title="users:invite">
        <form
          onSubmit={(ev) => {
            ev.preventDefault();
            invite.mutate(newIntent({ path: `/v1/admin/organizations/${organizationId}/users:invite`, body: { user_id: userId } }));
          }}
        >
          <Field label="user_id" name="invite-user" value={userId} onChange={setUserId} required ltr />
          {invite.error ? <ErrorState error={invite.error} /> : null}
          <button className="btn" type="submit" disabled={invite.isPending}>
            {t('action.create')}
          </button>
        </form>
      </Card>

      <Card title="organization lifecycle">
        <AuditStatement>
          <p>
            <Ltr>organization.manage</Ltr> — platform-scoped, and a compare-and-swap:{' '}
            <Ltr>If-Match=&quot;{orgVersion ?? '?'}&quot;</Ltr> is what makes a concurrent change a
            conflict rather than an overwrite. Both <Ltr>:transition</Ltr> and <Ltr>:verify</Ltr>
            require it.
          </p>
        </AuditStatement>
        {organization.isLoading ? <LoadingState rows={1} /> : null}
        {organization.error ? (
          <ErrorState error={organization.error} onRetry={() => void organization.refetch()} />
        ) : null}
        <div className="row">
          {['active', 'suspended', 'terminated'].map((to) => (
            <button
              key={to}
              type="button"
              className="btn btn--secondary"
              disabled={orgAction.isPending || orgVersion === null}
              onClick={() =>
                orgAction.mutate(newIntent({
                  path: `/v1/admin/organizations/${organizationId}:transition`,
                  body: { to, reason_code: reason, note: '' },
                  ifMatch: orgVersion as number,
                  label: `organization → ${to}`,
                }))
              }
            >
              {to}
            </button>
          ))}
          <button
            type="button"
            className="btn btn--secondary"
            disabled={orgAction.isPending || orgVersion === null}
            onClick={() =>
              orgAction.mutate(newIntent({
                path: `/v1/admin/organizations/${organizationId}:verify`,
                body: { verification_state: 'verified', reason_code: reason },
                ifMatch: orgVersion as number,
                label: 'organization → verified',
              }))
            }
          >
            verify
          </button>
        </div>
        {orgAction.error ? (
          <RefusalWithRemedy
            error={orgAction.error}
            action="organization.manage"
            retryExact={orgAction.retryLast}
            onReread={() => void organization.refetch()}
          />
        ) : null}
      </Card>
    </>
  );
}
